// src/types/validation.ts

export interface ValidationResult {
  isValid: boolean;
  category: 'blocker' | 'warning';
  rule: string;
  message: string;
  affectedImages: string[];
  details?: string;
}

export interface ValidationState {
  status: 'idle' | 'validating' | 'complete';
  results: ValidationResult[];
  canReconstruct: boolean;
  progress?: number;
  currentStep?: string;
}

export interface ImageDimensions {
  width: number;
  height: number;
  aspectRatio: number;
  isLandscape: boolean;
  megapixels: number;
}

export interface ValidationConfig {
  // File validation
  maxFileSizeBytes: number;
  allowedFormats: string[];
  
  // Image validation
  minImagesPerCamera: number;
  maxResolutionVariance: number; // percentage
  minAspectRatioForLandscape: number;
  
  // Quality thresholds
  minMegapixels: number;
  maxMegapixels: number;
  
  // Performance tuning for large image sets
  batchSize?: number;
  timeoutMs?: number;
  delayBetweenBatchesMs?: number;
}

export interface ImageValidationData {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  dimensions?: ImageDimensions;
  cameraId: string;
  validationErrors: ValidationResult[];
}

export interface FileTypeValidationConfig extends ValidationConfig {
  fileType: string;
  displayName: string;
}

// File type specific configurations
export const VALIDATION_CONFIGS: Record<string, FileTypeValidationConfig> = {
  jpeg: {
    fileType: 'jpeg',
    displayName: 'JPEG/JPG',
    maxFileSizeBytes: 20 * 1024 * 1024, // 15MB - standard for compressed images
    allowedFormats: ['image/jpeg', 'image/jpg'],
    minImagesPerCamera: 5,
    maxResolutionVariance: 15,
    minAspectRatioForLandscape: 1.1,
    minMegapixels: 2,
    maxMegapixels: 100,
    batchSize: 20,
    timeoutMs: 15000,
    delayBetweenBatchesMs: 50
  },
  heic: {
    fileType: 'heic',
    displayName: 'HEIC (iOS)',
    maxFileSizeBytes: 20 * 1024 * 1024, // 20MB - HEIC can be larger than JPEG
    allowedFormats: ['image/heic', 'image/heif'],
    minImagesPerCamera: 5,
    maxResolutionVariance: 15,
    minAspectRatioForLandscape: 1.1,
    minMegapixels: 2,
    maxMegapixels: 100,
    batchSize: 15, // Slower processing for HEIC
    timeoutMs: 20000,
    delayBetweenBatchesMs: 75
  },
  exr: {
    fileType: 'exr',
    displayName: 'EXR (HDR)',
    maxFileSizeBytes: 40 * 1024 * 1024, // 40MB - HDR files are much larger
    allowedFormats: ['.exr'], // EXR doesn't have standard MIME type
    minImagesPerCamera: 3, // Fewer images needed for HDR workflows
    maxResolutionVariance: 20, // Allow more variance for HDR captures
    minAspectRatioForLandscape: 1.0, // More flexible for HDR
    minMegapixels: 1, // HDR quality isn't just about megapixels
    maxMegapixels: 100, // HDR can go much higher
    batchSize: 5, // Much slower processing for EXR
    timeoutMs: 30000, // Longer timeout for large files
    delayBetweenBatchesMs: 100
  },
  png: {
    fileType: 'png',
    displayName: 'PNG',
    maxFileSizeBytes: 25 * 1024 * 1024, // 25MB - PNG can be large due to no compression
    allowedFormats: ['image/png'],
    minImagesPerCamera: 5,
    maxResolutionVariance: 15,
    minAspectRatioForLandscape: 1.1,
    minMegapixels: 2,
    maxMegapixels: 100,
    batchSize: 15,
    timeoutMs: 20000,
    delayBetweenBatchesMs: 75
  }
};

// Helper function to get the appropriate config based on detected file type
export const getValidationConfigForFileType = (fileType: string): ValidationConfig => {
  const config = VALIDATION_CONFIGS[fileType.toLowerCase()];
  if (config) {
    console.log(`🔧 [CONFIG] Using ${config.displayName} validation config (${config.maxFileSizeBytes / (1024 * 1024)}MB limit)`);
    return config;
  }
  
  // Fallback to JPEG config for unknown types
  console.log(`⚠️ [CONFIG] Unknown file type '${fileType}', using JPEG config as fallback`);
  return VALIDATION_CONFIGS.jpeg;
};

// Update the default to be JPEG-based
export const DEFAULT_VALIDATION_CONFIG: ValidationConfig = VALIDATION_CONFIGS.jpeg;